import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InternalChatbotService } from '../../../core/services/internal-chatbot.service';
import { InternalChatLog } from '../../../core/models/internal-chatbot.model';

@Component({
  selector: 'app-internal-chat-logs',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './internal-chat-logs.component.html',
  styleUrl: './internal-chat-logs.component.css'
})
export class ChatLogsComponent implements OnInit {

  // View state
  viewMode: 'all' | 'session' = 'all';
  selectedSessionId = '';

  // All logs
  allLogs: InternalChatLog[] = [];
  filteredLogs: InternalChatLog[] = [];
  pageData: InternalChatLog[] = [];
  loading = false;

  // Session logs
  sessionLogs: InternalChatLog[] = [];
  sessionLoading = false;

  // Pagination
  page = 1;
  rowsPerPage = 10;
  totalPages = 1;
  skeletonRows = Array(10);

  // Search / filter
  search = '';
  filterAnswered: 'all' | 'found' | 'not_found' = 'all';

  constructor(private internalChatbotService: InternalChatbotService) {}

  ngOnInit(): void {
    this.loadAllLogs();
  }

  // ── Data Loading ──────────────────────────────────────────────────────────

  loadAllLogs() {
    this.loading = true;
    this.internalChatbotService.getAllLogs().subscribe({
      next: (logs) => {
        this.allLogs = logs;
        this.applyFilters();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  viewSession(sessionId: string) {
    this.selectedSessionId = sessionId;
    this.viewMode = 'session';
    this.sessionLogs = [];
    this.sessionLoading = true;

    this.internalChatbotService.getSessionLogs(sessionId).subscribe({
      next: (logs) => {
        this.sessionLogs = logs;
        this.sessionLoading = false;
      },
      error: () => {
        this.sessionLoading = false;
      }
    });
  }

  backToAll() {
    this.viewMode = 'all';
    this.selectedSessionId = '';
    this.sessionLogs = [];
  }

  // ── Filtering & Pagination ────────────────────────────────────────────────

  onSearchChange() {
    this.page = 1;
    this.applyFilters();
  }

  onFilterChange() {
    this.page = 1;
    this.applyFilters();
  }

  private applyFilters() {
    const term = this.search.toLowerCase();
    this.filteredLogs = this.allLogs.filter(log => {
      const matchesSearch = !term ||
        log.userQuestion.toLowerCase().includes(term) ||
        log.sessionId.toLowerCase().includes(term) ||
        (log.userId || '').toLowerCase().includes(term) ||
        (log.topDocumentName || '').toLowerCase().includes(term);

      const matchesFilter =
        this.filterAnswered === 'all' ||
        (this.filterAnswered === 'found' && log.answerFound) ||
        (this.filterAnswered === 'not_found' && !log.answerFound);

      return matchesSearch && matchesFilter;
    });

    this.totalPages = Math.max(1, Math.ceil(this.filteredLogs.length / this.rowsPerPage));
    this.updatePageData();
  }

  private updatePageData() {
    const start = (this.page - 1) * this.rowsPerPage;
    this.pageData = this.filteredLogs.slice(start, start + this.rowsPerPage);
  }

  onPageChange() {
    this.updatePageData();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  getRowIndex(idx: number): number {
    return (this.page - 1) * this.rowsPerPage + idx + 1;
  }

  getRowClass(idx: number): string {
    return idx % 2 === 0 ? 'qu-tr-even' : 'qu-tr-odd';
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleString('en-PK', { dateStyle: 'medium', timeStyle: 'short' });
  }

  formatResponseTime(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  }

  truncate(text: string, max = 80): string {
    if (!text) return '—';
    return text.length > max ? text.slice(0, max) + '…' : text;
  }

  shortSession(sessionId: string): string {
    return sessionId.length > 16 ? sessionId.slice(0, 16) + '…' : sessionId;
  }

  get answeredCount(): number {
    return this.allLogs.filter(l => l.answerFound).length;
  }

  get unansweredCount(): number {
    return this.allLogs.filter(l => !l.answerFound).length;
  }

  get uniqueSessionCount(): number {
    return new Set(this.allLogs.map(l => l.sessionId)).size;
  }
}
